import { Avatar } from "orcs-design-system";
import styled from "styled-components";
import themeGet from "@styled-system/theme-get";
import stringToColor from "color-string";

import { DEFAULT_GRADIENT, GRADIENT_ATTRIBUTES } from "src/consts/gradient";
import { ENTITY_TYPES } from "src/consts/entityTypes";

export const TEAM_PROFILE_BG_GRADIENT_ATTRIBUTE_ID =
  GRADIENT_ATTRIBUTES.TEAM_PROFILE_BG;
export const DEFAULT_TEAM_PROFILE_BG_GRADIENT = DEFAULT_GRADIENT;

export const placeholderGradient = (props) =>
  `linear-gradient(135deg, ${themeGet("colors.primaryLightest")(
    props
  )}, ${themeGet("colors.primaryLight")(props)})`;

const normalizeGradientValue = (value) => {
  if (typeof value !== "string" || value.trim().length === 0) {
    return DEFAULT_GRADIENT;
  }

  const trimmedValue = value.trim();
  if (/gradient\s*\(/i.test(trimmedValue)) {
    return trimmedValue;
  }

  return `linear-gradient(90deg, ${trimmedValue} 0%, ${trimmedValue} 100%)`;
};

export const getBrandingGradient = (branding = {}) => {
  if (Array.isArray(branding.gradients)) {
    return normalizeGradientValue(branding.gradients[0]);
  }

  const value = branding?.gradients;
  if (typeof value === "string" && value.trim().length > 0) {
    return normalizeGradientValue(value);
  }

  return DEFAULT_GRADIENT;
};

// Generic function that works for any entity type
export const getEntityGradient = (entityType, branding, entity = {}) => {
  // Map "group" to "team" for gradient attribute lookup since they use the same gradient system
  const gradientEntityType = entityType === "group" ? "team" : entityType;

  // Check for entity-specific gradient attribute
  const gradientAttributeId =
    GRADIENT_ATTRIBUTES[`${gradientEntityType.toUpperCase()}_PROFILE_BG`];
  if (gradientAttributeId && entity?.attributes) {
    const entityGradient = entity.attributes?.find?.(
      (attribute) => attribute.id === gradientAttributeId
    )?.value;
    if (entityGradient) {
      return normalizeGradientValue(entityGradient);
    }
  }

  // Fall back to branding gradient or default
  const brandingGradient = getBrandingGradient(branding);
  return normalizeGradientValue(brandingGradient);
};

// Legacy wrappers for backward compatibility
export const getTeamGradient = (branding, group = {}) =>
  getEntityGradient(ENTITY_TYPES.TEAM, branding, group);

export const getPersonGradient = (branding, person = {}) =>
  getEntityGradient(ENTITY_TYPES.PERSON, branding, person);

export const getTagGradient = (branding, tag = {}) =>
  getEntityGradient(ENTITY_TYPES.TAG, branding, tag);

const getAvatarGradient = ({ bgGradient }) => bgGradient || DEFAULT_GRADIENT;

export const AvatarWithGradient = styled(Avatar)`
  > div:first-child {
    background-image: ${getAvatarGradient};
    background-size: cover;
    background-position: center center;

    > span {
      color: #fff;
    }
  }
`;

// A position seat, filled or vacant, never shows a real person avatar — just
// an empty circle whose border marks occupancy: solid when someone actually
// fills the seat (just hidden from view), dashed when the seat is genuinely
// vacant.
export const PositionSeatAvatar = styled(Avatar)`
  > div:first-child {
    background: none !important;
    background-image: none !important;
    border: 2px ${({ $filled }) => ($filled ? "solid" : "dashed")}
      ${themeGet("colors.greyDark")};
    box-sizing: border-box;
  }
`;

export const splitGradientArgs = (str) => {
  const result = [];
  let current = "";
  let depth = 0;

  for (const char of str) {
    if (char === "(") {
      depth++;
    }
    if (char === ")") {
      depth--;
    }
    if (char === "," && depth === 0) {
      result.push(current.trim());
      current = "";
    } else {
      current += char;
    }
  }

  if (current.trim()) {
    result.push(current.trim());
  }
  return result;
};

export const convertGradientToRgba = (gradientStr) => {
  if (
    !gradientStr.startsWith("linear-gradient") &&
    !gradientStr.startsWith("radial-gradient")
  ) {
    return gradientStr;
  }

  const type = gradientStr.startsWith("linear-gradient")
    ? "linear-gradient"
    : "radial-gradient";

  const innerStart = gradientStr.indexOf("(") + 1;
  const innerEnd = gradientStr.lastIndexOf(")");
  const innerContent = gradientStr.slice(innerStart, innerEnd).trim();

  const args = splitGradientArgs(innerContent);
  const firstArg = args[0];
  const isFirstColor = /^#|^rgba?\(|^rgb\(|^hsla?\(/.test(firstArg);

  const directionOrShape = isFirstColor ? null : firstArg;
  const colorStops = isFirstColor ? args : args.slice(1);
  const total = colorStops.length;

  const resultStops = colorStops.map((stop, i) => {
    const parts = stop.trim().split(/\s+(?![^(]*\))/); // splits on spaces not in parentheses
    const colorPart = parts[0];
    const hasPercentage = parts.length > 1;

    let color = colorPart;

    if (/^#/.test(colorPart)) {
      const rgb = stringToColor.get.rgb(colorPart);
      // default to white when invalid hex
      const DEFAULT_WHEN_INVALID_HEX_COLOR = [255, 255, 255];

      color = stringToColor.to.rgb(rgb || DEFAULT_WHEN_INVALID_HEX_COLOR);
    }

    let percent = "0%";

    if (hasPercentage) {
      percent = parts.slice(1).join(" ");
    } else if (total > 1) {
      percent = `${Math.round((i / (total - 1)) * 100)}%`;
    }

    return `${color} ${percent}`;
  });

  const newContent = directionOrShape
    ? `${directionOrShape}, ${resultStops.join(", ")}`
    : resultStops.join(", ");

  return `${type}(${newContent})`;
};

export const sanitizeGradientForColorPicker = (gradient) => {
  let sanitizedGradient = convertGradientToRgba(gradient);

  if (sanitizedGradient.endsWith(";")) {
    sanitizedGradient = sanitizedGradient.slice(0, -1);
  }

  return sanitizedGradient;
};
